import React, { useEffect, useState } from "react";
import { ApiClient } from "../../utils/api-client";
import Header from "../home/header";
import Loading from "../home/Loading";
import UTILS from "../../utils/commons";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { X, Pen } from 'lucide-react'

const TenderDetails = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const location = useLocation();

    const loginState = sessionStorage.getItem( "loginState" ) ? JSON.parse( sessionStorage.getItem( "loginState" ) ) : null;
    const apiClient = new ApiClient( loginState?.accessToken );

    const [ loading, setLoading ] = useState( false );
    const [ feedback, setFeedback ] = useState( "" );
    const [ tender, setTender ] = useState( location.state || null );

    const loadData = async () => {
        if ( !id ) return;
        setLoading( true );
        setFeedback( "" );
        try {
            const req = await apiClient.get( `tenders/${id}` );
            if ( req.status === 200 && req.data ) {
                setTender( req.data );
            } else {
                setFeedback( "Tender not found." );
            }
        } catch ( e ) {
            setFeedback( "Failed to load tender details." );
        } finally {
            setLoading( false );
        }
    };

    useEffect( () => {
        if ( !loginState ) {
            navigate( "/signin", { replace: true } );
            return;
        }
        loadData();
    }, [ id ] );

    const expenses = Array.isArray( tender?.expenses ) ? tender.expenses : [];
    const tenderProducts = Array.isArray( tender?.tenderProducts ) ? tender.tenderProducts : [];
    const totalExpenses = expenses.reduce( ( sum, ex ) => sum + Number( ex?.amount || 0 ), 0 );
    const tenderAmount = Number( tender?.tenderedAmount || 0 );
    const margin = tenderAmount - totalExpenses;

    return (
        <>
            <Header auth={loginState} />
            <section className="container mx-auto md:mt-12 shadow p-4 rounded-md">
                <div className="flex justify-between items-center mb-4">
                    <p className="text-2xl">Tender Details</p>
                    <div>
                        <button className="btn text-primary-500 hover:text-secondary-500" onClick={() => navigate( `/tenders/edit/${id}`, { replace: false, state: tender } )}><Pen /></button>
                        <button className="btn text-primary-500 hover:text-secondary-500" onClick={() => navigate( "/tenders", { replace: true } )}><X /></button>
                    </div>
                </div>

                {feedback ? <div className="alert text-center danger mb-4">{feedback}</div> : null}
                {loading ? <Loading /> : null}

                {!loading && tender ? (
                    <div className="grid md:grid-cols-2 gap-6">
                        <div className="bg-gray-50 p-4 rounded-md border">
                            <p className="font-semibold text-lg mb-3">Overview</p>
                            <div className="my-2"><label className="block text-sm text-gray-600">Title</label><p>{tender.title || "-"}</p></div>
                            <div className="my-2"><label className="block text-sm text-gray-600">Organisation</label><p>{tender.organisation || "-"}</p></div>
                            <div className="my-2"><label className="block text-sm text-gray-600">Status</label><p>{UTILS.tenderStatusBadge( tender.status )}</p></div>
                            <div className="my-2"><label className="block text-sm text-gray-600">Created</label><p>{tender.createdAt ? UTILS.formatDate( tender.createdAt ) : "-"}</p></div>
                        </div>

                        <div className="bg-gray-50 p-4 rounded-md border">
                            <p className="font-semibold text-lg mb-3">Financial Summary</p>
                            <div className="my-2"><label className="block text-sm text-gray-600">Tendered Amount (TZS)</label><p>{UTILS.thousandSeparator( tenderAmount || 0 )}</p></div>
                            <div className="my-2"><label className="block text-sm text-gray-600">Expenses Incurred (TZS)</label><p>{UTILS.thousandSeparator( totalExpenses || 0 )}</p></div>
                            <div className="my-2"><label className="block text-sm text-gray-600">Balance (TZS)</label><p className={margin < 0 ? "text-red-600" : "text-green-700"}>{UTILS.thousandSeparator( margin )}</p></div>
                        </div>

                        <div className="bg-gray-50 p-4 rounded-md border md:col-span-2">
                            <p className="font-semibold text-lg mb-3">Products</p>
                            {tenderProducts.length === 0 ? <p className="text-gray-500">No products attached.</p> : (
                                <div className="grid md:grid-cols-3 gap-2">
                                    {tenderProducts.map( ( tp, idx ) => (
                                        <div key={tp.productId || idx} className="border rounded p-2 bg-white">
                                            <p className="font-medium">{tp?.product?.productName} - {tp?.product?.packSize}</p>
                                            <p className="text-sm text-gray-600">Quantity: {tp?.quantity || 0}</p>
                                        </div>
                                    ) )}
                                </div>
                            )}
                        </div>

                        <div className="bg-gray-50 p-4 rounded-md border">
                            <p className="font-semibold text-lg mb-3">Bank Loan</p>
                            <div className="my-2"><label className="block text-sm text-gray-600">Bank Name</label><p>{tender?.bankLoan?.bankName || "-"}</p></div>
                            <div className="my-2"><label className="block text-sm text-gray-600">Loan Amount (TZS)</label><p>{tender?.bankLoan?.loanAmount ? UTILS.thousandSeparator( tender.bankLoan.loanAmount ) : "-"}</p></div>
                            <div className="my-2"><label className="block text-sm text-gray-600">Interest</label><p>{tender?.bankLoan?.interest ? `${tender.bankLoan.interest}%` : "-"}</p></div>
                            <div className="my-2"><label className="block text-sm text-gray-600">Tenure</label><p>{tender?.bankLoan?.tenure ? `${tender.bankLoan.tenure} months` : "-"}</p></div>
                        </div>

                        <div className="bg-gray-50 p-4 rounded-md border">
                            <p className="font-semibold text-lg mb-3">Expense Items</p>
                            {expenses.length === 0 ? <p className="text-gray-500">No expenses recorded.</p> : (
                                <div className="space-y-2">
                                    {expenses.map( ( ex, idx ) => (
                                        <div key={idx} className="flex justify-between border-b pb-1">
                                            <span>{ex.description || "-"}</span>
                                            <span>{UTILS.thousandSeparator( Number( ex.amount || 0 ) )}</span>
                                        </div>
                                    ) )}
                                </div>
                            )}
                        </div>
                    </div>
                ) : null}
            </section>
        </>
    );
};
export default TenderDetails;
